import { subDays } from "@prismicio/editor-support/Date"
import {
  Blockquote,
  Button,
  Skeleton,
  Text,
  Tooltip,
  useToast,
} from "@prismicio/editor-ui"
import { type PropsWithChildren, useState } from "react"

import { requestUsageLogs } from "../../app/services/usage"
import {
  type usageSummaryItemTypes,
  usageSummarySubItemTypes,
} from "./usageSummaryItemTypes"

export interface CurrentPlanCardSubItemProps {
  itemTypeKey: keyof typeof usageSummaryItemTypes
  subItemTypeKey: keyof typeof usageSummarySubItemTypes
}

export function CurrentPlanCardSubItem(props: CurrentPlanCardSubItemProps) {
  const { itemTypeKey, subItemTypeKey } = props

  const { showToast } = useToast()
  const [requested, setRequested] = useState(false)
  const [loading, setLoading] = useState(false)

  const { name, itemTypes } = usageSummarySubItemTypes[subItemTypeKey]
  const { logsDescription } = itemTypes[itemTypeKey]

  return (
    <CurrentPlanCardSubItemTemplate>
      <Text component="h6" variant="bold">
        {name}
      </Text>
      <Text color="grey11" variant="small">
        View your {logsDescription} over the last 30 days.
      </Text>
      <Tooltip
        content="You've already requested the logs. Please check your email soon."
        visible={requested}
        sideOffset={4}
      >
        <Button
          disabled={requested}
          invisible
          loading={loading}
          onClick={async () => {
            setLoading(true)

            const endDate = new Date()
            try {
              await requestUsageLogs({
                startDate: subDays(endDate, 30),
                endDate,
                data: itemToLogsData[itemTypeKey],
                type: subItemToLogsType[subItemTypeKey],
              })
            } catch {
              const title =
                "There was an error requesting your CSV file. Please try again later."
              showToast({ icon: "alert", title })

              setLoading(false)
              return
            }

            const title =
              "A CSV file with your logs will be emailed to you. During peak times, this may take a few hours."
            showToast({ title })

            setLoading(false)
            setRequested(true)
          }}
          size="small"
          sx={{ alignSelf: "start" }}
          textColor="link"
          textWeight="normal"
        >
          Download logs
        </Button>
      </Tooltip>
    </CurrentPlanCardSubItemTemplate>
  )
}

function CurrentPlanCardSubItemTemplate(props: PropsWithChildren) {
  return <Blockquote {...props} />
}

const itemToLogsData = {
  "cdn-bandwidth": "bandwidth",
  "api-calls": "calls",
} as const

const subItemToLogsType = {
  "content-api": "CloudFront",
  "image-cdn": "Imgix",
} as const

export function CurrentPlanCardSubItemSkeleton() {
  return (
    <CurrentPlanCardSubItemTemplate>
      <Skeleton width={80} height={24} />
      <Skeleton height={16} />
      <Skeleton width={83} height={16} />
    </CurrentPlanCardSubItemTemplate>
  )
}
